"use client";

import { ArrowRight, FileText, Laptop, Rocket, Smartphone, Sparkles, Tablet, TrendingUp } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RevenueManagementPillars() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const smallCards = [{
    icon: FileText,
    title: "OTA Revenue Audit",
    description: "Deep analysis of your OTA performance to identify hidden revenue leaks and growth.",
    gradient: "from-[var(--chart-1)] to-[var(--chart-4)]",
    orbColor: "var(--chart-1)"
  }, {
    icon: TrendingUp,
    title: "OTA Strategy",
    description: "Data-driven channel management to maximize visibility and direct booking conversion.",
    gradient: "from-[var(--chart-2)] to-[var(--chart-3)]",
    orbColor: "var(--chart-2)"
  }, {
    icon: Smartphone,
    title: "Tech Integration",
    description: "Expert selection and setup of hospitality tech stacks to streamline operations.",
    gradient: "from-[var(--chart-3)] to-[var(--chart-1)]",
    orbColor: "var(--chart-3)"
  }, {
    icon: Rocket,
    title: "Revenue Growth",
    description: "Hands-on management to scale your property revenue and improve market share.",
    gradient: "from-[var(--chart-4)] to-[var(--chart-2)]",
    orbColor: "var(--chart-4)"
  }];
  return <section data-section-id="183" id="revenuemanagementpillars" className="relative overflow-hidden bg-background py-20 sm:py-28 lg:py-32">
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[500px] w-[500px] -translate-x-1/2 rounded-lg opacity-30 blur-3xl" style={{
      background: "radial-gradient(circle, var(--primary) 0%, transparent 70%)"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-700 ${headerVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          <div className="inline-flex items-center gap-2 rounded-lg border border-border bg-card/50 px-4 py-1.5 backdrop-blur-sm">
            <Sparkles className="h-3.5 w-3.5" style={{
            color: "var(--primary)"
          }} aria-hidden="true" />
            <span className="font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground">
              Our Expertise
            </span>
          </div>
          <h2 className="font-default mt-6 text-4xl font-semibold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            Revenue{" "}
            <span className="bg-clip-text text-transparent" style={{
            backgroundImage: "linear-gradient(135deg, var(--primary) 0%, var(--chart-2) 50%, var(--chart-3) 100%)"
          }}>
              Optimization
            </span>
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
            We help hotel owners and investors maximize revenue through data-driven 
            OTA strategies and modern hospitality technology implementation.
          </p>
        </div>

        <div ref={gridRef} className={`mt-16 grid grid-cols-1 gap-5 transition-all duration-1000 sm:mt-20 md:grid-cols-2 lg:grid-cols-3 lg:gap-6 ${gridVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <div data-index={0} className="group relative col-span-1 overflow-hidden rounded-2xl border border-border bg-card p-8 transition-all duration-500 hover:-translate-y-1 hover:border-transparent md:col-span-2" style={{
          boxShadow: "var(--shadow-lg)"
        }}>
            <div className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100" style={{
            background: "linear-gradient(135deg, var(--primary), var(--chart-2), var(--chart-3))",
            padding: "1px",
            WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude"
          }} aria-hidden="true" />

            <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-lg opacity-40 blur-2xl transition-all duration-500 group-hover:opacity-80" style={{
            background: "radial-gradient(circle, var(--primary) 0%, transparent 70%)"
          }} aria-hidden="true" />

            <div className="relative">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl" style={{
              background: "linear-gradient(135deg, hsl(43 72% 52% / 0.15), hsl(222 47% 25% / 0.15))",
              border: "1px solid hsl(43 72% 52% / 0.2)"
            }}>
                <Sparkles className="h-6 w-6" style={{
                color: "var(--primary)"
              }} aria-hidden="true" />
              </div>

              <h3 className="font-default mt-6 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
                OTA Revenue Management
              </h3>
              <p className="mt-3 max-w-xl text-base leading-relaxed text-muted-foreground">
                We manage your OTA presence to ensure optimal pricing, inventory 
                control, and visibility, turning your digital channels into 
                high-performing revenue assets.
              </p>

              <Button asChild variant="link" className="mt-5 h-auto p-0 text-base font-medium" style={{
              color: "var(--primary)"
            }}>
                <Link to="/ServicesPage" className="group/link inline-flex items-center gap-1.5">
                  View services
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-1" />
                </Link>
              </Button>
            </div>

            <div className="relative mt-10 -mb-12 sm:-mb-16">
              <div className="relative mx-auto h-48 max-w-2xl overflow-hidden rounded-t-xl border border-border bg-background/80 backdrop-blur-sm sm:h-56" style={{
              boxShadow: "var(--shadow-2xl)"
            }}>
                <div className="flex items-center gap-1.5 border-b border-border px-4 py-3">
                  <div className="h-2.5 w-2.5 rounded-lg bg-muted-foreground/30" />
                  <div className="h-2.5 w-2.5 rounded-lg bg-muted-foreground/30" />
                  <div className="h-2.5 w-2.5 rounded-lg bg-muted-foreground/30" />
                </div>

                <div className="grid grid-cols-12 gap-3 p-5">
                  <div className="col-span-3 space-y-2.5">
                    <div className="h-3 rounded-md bg-muted" />
                    <div className="h-3 w-3/4 rounded-md bg-muted" />
                    <div className="h-3 w-1/2 rounded-md bg-muted" />
                    <div className="mt-4 h-3 rounded-md bg-muted" />
                    <div className="h-3 w-2/3 rounded-md bg-muted" />
                  </div>
                  <div className="col-span-9 space-y-3">
                    <div className="h-16 rounded-lg" style={{
                    background: "linear-gradient(135deg, hsl(43 72% 52% / 0.2), hsl(222 47% 25% / 0.15))"
                  }} />
                    <div className="grid grid-cols-3 gap-3">
                      <div className="h-14 rounded-lg bg-muted" />
                      <div className="h-14 rounded-lg bg-muted" />
                      <div className="h-14 rounded-lg bg-muted" />
                    </div>
                    <div className="h-3 w-5/6 rounded-md bg-muted" />
                    <div className="h-3 w-4/6 rounded-md bg-muted" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {smallCards.map((card, i) => {
          const Icon = card.icon;
          return <div key={card.title} data-index={i + 1} className="group relative overflow-hidden rounded-2xl border border-border bg-card p-8 transition-all duration-500 hover:-translate-y-1 hover:border-transparent" style={{
            boxShadow: "var(--shadow)"
          }}>
                <div className={`pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br ${card.gradient} opacity-0 transition-opacity duration-500 group-hover:opacity-100`} style={{
              padding: "1px",
              WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
              WebkitMaskComposite: "xor",
              maskComposite: "exclude"
            }} aria-hidden="true" />

                <div className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-lg opacity-30 blur-2xl transition-all duration-500 group-hover:opacity-70" style={{
              background: `radial-gradient(circle, ${card.orbColor} 0%, transparent 70%)`
            }} aria-hidden="true" />

                <div className="relative">
                  <div className={`flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br ${card.gradient}`} style={{
                boxShadow: `0 8px 24px -8px ${card.orbColor}`
              }}>
                    <Icon className="h-6 w-6 text-white" strokeWidth={2} aria-hidden="true" />
                  </div>

                  <h3 className="font-default mt-6 text-xl font-semibold tracking-tight text-foreground">
                    {card.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {card.description}
                  </p>

                  <div className="mt-6 h-16 overflow-hidden">
                    {i === 0 && <div className="space-y-2">
                        <div className="h-2 w-full rounded-md" style={{
                    background: "linear-gradient(90deg, var(--primary), transparent)"
                  }} />
                        <div className="h-2 w-4/5 rounded-md" style={{
                    background: "linear-gradient(90deg, var(--chart-4), transparent)"
                  }} />
                        <div className="h-2 w-3/5 rounded-md" style={{
                    background: "linear-gradient(90deg, var(--primary), transparent)"
                  }} />
                      </div>}
                    {i === 1 && <div className="relative h-full">
                        <svg viewBox="0 0 120 60" className="h-full w-full" preserveAspectRatio="none" aria-hidden="true">
                          <defs>
                            <linearGradient id="seoGrad" x1="0" y1="0" x2="1" y2="0">
                              <stop offset="0%" stopColor="var(--chart-2)" />
                              <stop offset="100%" stopColor="var(--chart-3)" />
                            </linearGradient>
                          </defs>
                          <polyline points="0,50 20,42 40,38 60,28 80,22 100,12 120,5" fill="none" stroke="url(#seoGrad)" strokeWidth="2.5" strokeLinecap="round" />
                          <polyline points="0,50 20,42 40,38 60,28 80,22 100,12 120,5 120,60 0,60" fill="url(#seoGrad)" opacity="0.15" />
                        </svg>
                      </div>}
                    {i === 2 && <div className="flex items-end gap-2">
                        <div className="flex h-12 w-16 items-center justify-center rounded-md" style={{
                    background: "linear-gradient(135deg, hsl(199 89% 48% / 0.2), hsl(43 72% 52% / 0.15))",
                    border: "1px solid hsl(199 89% 48% / 0.3)"
                  }}>
                          <Laptop className="h-5 w-5" style={{
                      color: "var(--chart-3)"
                    }} />
                        </div>
                        <div className="flex h-10 w-9 items-center justify-center rounded-md" style={{
                    background: "linear-gradient(135deg, hsl(43 72% 52% / 0.2), hsl(199 89% 48% / 0.15))",
                    border: "1px solid hsl(43 72% 52% / 0.3)"
                  }}>
                          <Tablet className="h-4 w-4" style={{
                      color: "var(--primary)"
                    }} />
                        </div>
                        <div className="flex h-8 w-5 items-center justify-center rounded-md" style={{
                    background: "linear-gradient(135deg, hsl(142 71% 45% / 0.2), hsl(43 72% 52% / 0.15))",
                    border: "1px solid hsl(142 71% 45% / 0.3)"
                  }}>
                          <Smartphone className="h-3 w-3" style={{
                      color: "var(--chart-4)"
                    }} />
                        </div>
                      </div>}
                    {i === 3 && <div className="relative flex h-full items-center">
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg" style={{
                    background: "linear-gradient(135deg, var(--chart-4), var(--chart-2))",
                    boxShadow: "0 8px 20px -6px var(--chart-4)"
                  }}>
                          <Rocket className="h-6 w-6 text-white" strokeWidth={2} />
                        </div>
                        <div className="ml-3 h-1 flex-1 rounded-lg" style={{
                    background: "linear-gradient(90deg, var(--chart-4), transparent)"
                  }} />
                      </div>}
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}